import { createContext, useContext, useReducer } from 'react';

import {
  CLEAR_FNOL_CONTEXT,
  CLEAR_FNOL_MARKDOWN,
  SET_EMAIL,
  SET_FNOL_ANSWERS,
  SET_FNOL_CATEGORIES,
  SET_FNOL_INTRO_MARKDOWN,
  SET_FNOL_TEMPLATE,
  GET_FNOL_TEMPLATES,
  SET_INCIDENT_DAY,
  SET_INSURED_OBJECT,
  SET_SELECTED_FNOL_TEMPLATE,
  SET_STEP,
  SET_TELEPHONE,
  SET_SELECTED_FNOL_CATEGORY,
  SET_INSURANCE_HOLDER,
} from './types';
import { STEP_SELECT_FNOL_CATEGORY } from '../CONSTANTS';
import fnolServices from '../services/fnolServices';

const FnolContext = createContext();

const fnolReducer = (state, action) => {
  switch (action.type) {
    case CLEAR_FNOL_CONTEXT:
      return action.payload;
    case CLEAR_FNOL_MARKDOWN:
      return {
        ...state,
        fnolMarkdown: null,
      };
    case SET_FNOL_ANSWERS:
      return {
        ...state,
        fnolAnswers: {
          ...state.fnolAnswers,
          ...action.payload,
        },
      };
    case SET_FNOL_CATEGORIES:
      return {
        ...state,
        fnolCategories: action.payload,
      };
    case SET_FNOL_INTRO_MARKDOWN:
      return {
        ...state,
        fnolIntroMarkdown: action.payload,
      };
    case SET_FNOL_TEMPLATE:
      return {
        ...state,
        fnolTemplate: action.payload,
      };
    case GET_FNOL_TEMPLATES:
      return {
        ...state,
        fnolTemplates: action.payload,
      };
    case SET_EMAIL:
      return {
        ...state,
        customerInformation: {
          ...state.customerInformation,
          email: action.payload,
        },
      };
    case SET_INCIDENT_DAY:
      return {
        ...state,
        incidentDay: action.payload,
      };
    case SET_INSURANCE_HOLDER:
      return {
        ...state,
        customerInformation: {
          ...state.customerInformation,
          insuranceHolder: action.payload,
        },
      };
    case SET_INSURED_OBJECT:
      return {
        ...state,
        customerInformation: {
          ...state.customerInformation,
          insuredObject: action.payload,
        },
      };
    case SET_SELECTED_FNOL_CATEGORY:
      return {
        ...state,
        selectedFnolCategory: action.payload,
      };
    case SET_SELECTED_FNOL_TEMPLATE:
      return {
        ...state,
        selectedFnolTemplate: action.payload,
      };
    case SET_STEP:
      return {
        ...state,
        step: action.payload,
      };
    case SET_TELEPHONE:
      return {
        ...state,
        customerInformation: {
          ...state.customerInformation,
          telephone: action.payload,
        },
      };
    default:
      return state;
  }
};

const FnolProvider = ({ children }) => {
  const initialState = {
    customerInformation: {
      email: '',
      insuranceHolder: '',
      insuredObject: '',
      telephone: '',
    },
    fnolAnswers: {},
    fnolIntroMarkdown: null,
    fnolOutroMarkdown: null,
    fnolTemplate: null,
    fnolTemplates: [],
    fnolCategories: [],
    incidentDay: null,
    selectedFnolTemplate: null,
    selectedFnolCategory: null,
    step: STEP_SELECT_FNOL_CATEGORY,
  };

  const [state, dispatch] = useReducer(fnolReducer, initialState);

  const getFnolTemplates = async () => {
    try {
      const response = await fnolServices.getFnolTemplates();
      dispatch({ type: GET_FNOL_TEMPLATES, payload: response.data });
    } catch (err) {
      console.log(err);
    }
  };

  const setSelectedFnolTemplate = (fnolTemplate) =>
    dispatch({
      type: SET_SELECTED_FNOL_TEMPLATE,
      payload: fnolTemplate,
    });

  const setFnolCategories = () =>
    dispatch({
      type: SET_FNOL_CATEGORIES,
      payload: [
        ...new Map(
          state.fnolTemplates.map((template) => [
            template.category.key,
            template.category,
          ])
        ).values(),
      ],
    });

  const setSelectedFnolCategory = (fnolCategoryKey) =>
    dispatch({ type: SET_SELECTED_FNOL_CATEGORY, payload: fnolCategoryKey });

  const setFnolAnswers = (fnolAnswers) =>
    dispatch({ type: SET_FNOL_ANSWERS, payload: fnolAnswers });

  const setFnolTemplate = (fnolTemplate) =>
    dispatch({
      type: SET_FNOL_TEMPLATE,
      payload: fnolTemplate,
    });

  const setEmail = (email) => dispatch({ type: SET_EMAIL, payload: email });

  const setIncidentDay = (incidentDay) =>
    dispatch({ type: SET_INCIDENT_DAY, payload: incidentDay });

  const setInsuranceHolder = (insuranceHolder) =>
    dispatch({ type: SET_INSURANCE_HOLDER, payload: insuranceHolder });

  const setInsuredObject = (insuredObject) =>
    dispatch({ type: SET_INSURED_OBJECT, payload: insuredObject });

  const setStep = (step) => dispatch({ type: SET_STEP, payload: step });

  const setTelephone = (telephone) =>
    dispatch({ type: SET_TELEPHONE, payload: telephone });

  const setFnolIntroMarkdown = (markdown) =>
    dispatch({ type: SET_FNOL_INTRO_MARKDOWN, payload: markdown });

  const clearFnolMarkdown = () => dispatch({ type: CLEAR_FNOL_MARKDOWN });

  const clearFnolContext = () =>
    dispatch({
      type: CLEAR_FNOL_CONTEXT,
      payload: initialState,
    });

  return (
    <FnolContext.Provider
      value={{
        incidentDay: state.incidentDay,
        customerInformation: state.customerInformation,
        fnolAnswers: state.fnolAnswers,
        fnolCategories: state.fnolCategories,
        fnolIntroMarkdown: state.fnolIntroMarkdown,
        fnolOutroMarkdown: state.fnolOutroMarkdown,
        fnolTemplate: state.fnolTemplate,
        fnolTemplates: state.fnolTemplates,
        selectedFnolCategory: state.selectedFnolCategory,
        selectedFnolTemplate: state.selectedFnolTemplate,
        step: state.step,
        clearFnolContext,
        clearFnolMarkdown,
        getFnolTemplates,
        setEmail,
        setFnolAnswers,
        setFnolCategories,
        setFnolIntroMarkdown,
        setFnolTemplate,
        setIncidentDay,
        setInsuranceHolder,
        setInsuredObject,
        setSelectedFnolCategory,
        setSelectedFnolTemplate,
        setStep,
        setTelephone,
      }}
    >
      {children}
    </FnolContext.Provider>
  );
};

const useFnol = () => useContext(FnolContext);

export { FnolProvider, useFnol };
